import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import CompanyRecordFormDialog from "@/components/dialogs/CompanyRecordFormDialog";
import api, { fileUrl, downloadUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Eye, Download, Upload, Trash2, AlertTriangle } from "lucide-react";

const CATEGORIES = ["All", "Grant", "Certification", "MoU", "Legal Contract", "License"];

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function CompanyRecords() {
  const [records, setRecords] = useState([]);
  const [category, setCategory] = useState("All");
  const [formOpen, setFormOpen] = useState(false);

  const load = () => {
    const params = category !== "All" ? { category } : {};
    api.get("/company-records", { params }).then((r) => setRecords(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, [category]);

  const uploadNewVersion = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/company-records/${id}/new-version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New version uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteRecord = async (id) => {
    try {
      await api.delete(`/company-records/${id}`);
      toast.success("Record deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Company Knowledge Repository">
      <div className="flex items-center justify-between mb-5">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="record-category-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="add-record-button" onClick={() => setFormOpen(true)} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Record
        </Button>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="company-records-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-5 py-3 font-medium">Title</th>
              <th className="px-5 py-3 font-medium">Category</th>
              <th className="px-5 py-3 font-medium">Issue Date</th>
              <th className="px-5 py-3 font-medium">Expiry Date</th>
              <th className="px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[#4B5563]">No records found.</td></tr>}
            {records.map((r) => {
              const d = daysUntil(r.expiry_date);
              const expiring = d !== null && d <= 30;
              return (
                <tr key={r.id} data-testid={`record-row-${r.id}`} className="border-b border-[#E2E8F0] last:border-b-0">
                  <td className="px-5 py-3 font-medium text-[#1C1C1E]">{r.title}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{r.category}</td>
                  <td className="px-5 py-3 text-[#4B5563]">{r.issue_date || "—"}</td>
                  <td className="px-5 py-3">
                    {r.expiry_date ? (
                      <span className={`inline-flex items-center gap-1 ${expiring ? "text-red-600 font-medium" : "text-[#4B5563]"}`}>
                        {expiring && <AlertTriangle className="h-3.5 w-3.5" />} {r.expiry_date}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <a href={fileUrl(r.file_path)} target="_blank" rel="noreferrer" data-testid={`record-view-${r.id}`} title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                      <a href={downloadUrl(r.file_path, r.title)} data-testid={`record-download-${r.id}`} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                      <label className="cursor-pointer text-[#4B5563]" data-testid={`record-new-version-${r.id}`}>
                        <Upload className="h-4 w-4" />
                        <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadNewVersion(r.id, e.target.files[0])} />
                      </label>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`record-delete-${r.id}`} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{r.title}"?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteRecord(r.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CompanyRecordFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
    </DashboardLayout>
  );
}
